import { useCallback, useId, useRef, useState } from 'react';
import Calendar from './Calendar';
import PickerField from './PickerField';
import Popover from './Popover';
import { clampToRange, isDayDisabled, startOfDay } from './calendarUtils';
import { formatDateBy, toDate } from '../../utils/dates';

const DatePicker = ({
    value,
    onChange,
    placeholder = 'Select date',
    format = 'dd MMM yyyy',
    minDate,
    maxDate,
    excludeIntervals = [],
    monthsShown = 1,
    clearable = false,
    disabled = false,
    invalid = false,
    size = 'md',
    className,
    ariaLabel,
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const baseId = useId();
    const titleId = `${baseId}-title`;
    const popupId = `${baseId}-popup`;
    const date = toDate(value);

    const close = useCallback((reason) => {
        setOpen(false);
        if (reason === 'escape' || reason === 'select') {
            anchorRef.current?.querySelector('.dt-field__input')?.focus();
        }
    }, []);

    const handleSelect = (selected) => {
        // Preserve any time already on the value — DateTimeField relies on this
        // so picking a new day doesn't wipe the chosen time.
        const next = startOfDay(selected);
        if (date) next.setHours(date.getHours(), date.getMinutes(), 0, 0);
        onChange(next);
        close('select');
    };

    const today = startOfDay(new Date());
    const todayDisabled = isDayDisabled(today, { minDate, maxDate, excludeIntervals });

    return (
        <>
            <PickerField
                ref={anchorRef}
                icon="fa-calendar-days"
                value={formatDateBy(date, format)}
                placeholder={placeholder}
                open={open}
                popupId={popupId}
                disabled={disabled}
                invalid={invalid}
                clearable={clearable}
                size={size}
                className={className}
                ariaLabel={ariaLabel || placeholder}
                onOpen={() => setOpen(true)}
                onClear={() => onChange(null)}
            />

            <Popover
                anchorRef={anchorRef}
                id={popupId}
                open={open}
                onClose={close}
                labelledBy={titleId}
                className="dt-popover--calendar"
            >
                <Calendar
                    mode="single"
                    value={date}
                    onSelect={handleSelect}
                    minDate={minDate}
                    maxDate={maxDate}
                    excludeIntervals={excludeIntervals}
                    monthsShown={monthsShown}
                    titleId={titleId}
                />

                <div className="dt-panel__footer">
                    <button
                        type="button"
                        className="dt-panel__action"
                        disabled={todayDisabled}
                        onClick={() => handleSelect(clampToRange(today, minDate, maxDate))}
                    >
                        Today
                    </button>
                    {clearable && (
                        <button
                            type="button"
                            className="dt-panel__action"
                            onClick={() => {
                                onChange(null);
                                close('select');
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </Popover>
        </>
    );
};

export default DatePicker;
